// src/pages/AdminSupport/AdminSupport.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  ButtonGroup,
  Modal,
  Form,
} from "react-bootstrap";
import { Auth } from "aws-amplify";
import AdminNav from "../../components/AdminNav";
import "./AdminSupport.css";

const API_BASE = process.env.REACT_APP_API_BASE;

async function authHeader() {
  const session = await Auth.currentSession();
  // ✅ Use access token for API Gateway authorizer consistency (matches your other admin pages)
  const token = session.getAccessToken().getJwtToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function statusBadge(status) {
  const s = String(status || "").toLowerCase();
  if (s === "open") return <Badge bg="danger">Open</Badge>;
  if (s === "in_progress") return <Badge bg="warning">In Progress</Badge>;
  if (s === "closed") return <Badge bg="success">Closed</Badge>;
  return <Badge bg="secondary">{status || "unknown"}</Badge>;
}

function fmtDate(msOrIso) {
  if (!msOrIso) return "—";
  const d = typeof msOrIso === "number" ? new Date(msOrIso) : new Date(msOrIso);
  if (Number.isNaN(d.getTime())) return String(msOrIso);
  return d.toLocaleString();
}

export default function AdminSupport() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [filter, setFilter] = useState("open"); // open | in_progress | closed | all

  // SMS modal state
  const [messageModal, setMessageModal] = useState({
    show: false,
    ticket: null,
    text: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const idPayload = user?.signInUserSession?.idToken?.payload || {};
        const groups = idPayload["cognito:groups"] || idPayload["custom:groups"] || [];

        if (Array.isArray(groups) && (groups.includes("Support") || groups.includes("Admin"))) {
          setAuthorized(true);
          await fetchTickets("open");
        } else {
          setAuthorized(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Auth error:", err);
        setAuthorized(false);
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTickets = async (status = filter) => {
    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      if (!API_BASE) {
        throw new Error("Missing REACT_APP_API_BASE. Add it to your env and restart the dev server.");
      }

      const headers = await authHeader();
      let url = `${API_BASE}/admin/support/tickets`;

      if (status && status !== "all") {
        url += `?status=${encodeURIComponent(status)}`;
      } else {
        url += `?status=all`;
      }

      const res = await fetch(url, { method: "GET", headers });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load tickets: ${res.status}`);
      }

      const data = await res.json();
      setTickets(data.items || []);
    } catch (err) {
      console.error("Fetch tickets error:", err);
      setError(err?.message || "Failed to load tickets.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (ticketId, status) => {
    setError("");
    setSuccessMsg("");

    try {
      const headers = await authHeader();
      const res = await fetch(
        `${API_BASE}/admin/support/tickets/${encodeURIComponent(ticketId)}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({ status }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to update status: ${res.status}`);
      }

      setTickets((prev) => prev.map((t) => (t.ticketId === ticketId ? { ...t, status } : t)));
      setSuccessMsg(`Updated ticket ${ticketId} → ${status}.`);
    } catch (err) {
      console.error("Update status error:", err);
      setError(err?.message || "Failed to update status.");
    }
  };

  const onFilterChange = async (newFilter) => {
    setFilter(newFilter);
    await fetchTickets(newFilter);
  };

  const openMessageModal = (ticket) => {
    setError("");
    setSuccessMsg("");
    setMessageModal({
      show: true,
      ticket,
      text: `Hi, this is Little Paws Place regarding SmartTag ${ticket.smartTagId || ""}...`,
    });
  };

  const closeMessageModal = () => {
    setMessageModal({ show: false, ticket: null, text: "" });
  };

  const sendMessageToTag = async () => {
    const tagId = messageModal.ticket?.smartTagId;
    const message = (messageModal.text || "").trim();

    if (!tagId) {
      setError("This ticket has no SmartTag ID to message.");
      return;
    }
    if (!message) {
      setError("Message cannot be empty.");
      return;
    }

    setSending(true);
    setError("");
    setSuccessMsg("");

    try {
      const headers = await authHeader();
      const res = await fetch(
        `${API_BASE}/admin/tags/${encodeURIComponent(tagId)}/message`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ message }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to send message: ${res.status}`);
      }

      setSuccessMsg(`Message sent to owner of ${tagId}.`);
      closeMessageModal();
    } catch (err) {
      console.error("sendMessageToTag error:", err);
      setError(err?.message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const filterLabel = useMemo(() => {
    return (
      {
        open: "Open",
        in_progress: "In Progress",
        closed: "Closed",
        all: "All",
      }[filter] || "Open"
    );
  }, [filter]);

  if (loading && !tickets.length) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4 text-center">
            <Spinner animation="border" /> Loading support tickets…
          </div>
        </Container>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4">
            <Alert variant="danger">You’re not authorized to view this page.</Alert>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Container className="admin-page-shell">
        <AdminNav />

        <div className="admin-page-header">
          <div className="admin-page-header-left">
            <div className="admin-page-header-icon">🎫</div>
            <div>
              <h3 className="admin-page-title">Support Tickets – {filterLabel}</h3>
              <div className="admin-page-subtitle">
                Filter by status to see open, in-progress, or closed tickets.
              </div>
            </div>
          </div>

          <div className="admin-page-header-right">
            <Button variant="outline-secondary" size="sm" onClick={() => fetchTickets(filter)}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="admin-page-body">
          <div className="admin-panel mb-3">
            <div className="admin-filter-row">
              <div />
              <div className="admin-filter-actions">
                <ButtonGroup size="sm">
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "open" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("open")}
                  >
                    Open
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "in_progress" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("in_progress")}
                  >
                    In Progress
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "closed" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("closed")}
                  >
                    Closed
                  </Button>
                  <Button
                    className="admin-filter-btn"
                    variant={filter === "all" ? "primary" : "outline-primary"}
                    onClick={() => onFilterChange("all")}
                  >
                    All
                  </Button>
                </ButtonGroup>

                <Button variant="outline-secondary" size="sm" onClick={() => fetchTickets(filter)}>
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          <div className="admin-panel">
            {tickets.length === 0 ? (
              <p className="mb-0">No tickets for this filter 🎉</p>
            ) : (
              <Table className="admin-table" striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Ticket ID</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>SmartTag ID</th>
                    <th>Topic</th>
                    <th>Problem</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.ticketId}>
                      <td style={{ maxWidth: 240, wordBreak: "break-all" }}>{t.ticketId}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{statusBadge(t.status)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{fmtDate(t.createdAt)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{t.name || "—"}</td>
                      <td style={{ maxWidth: 220, wordBreak: "break-word" }}>{t.contact || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{t.smartTagId || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{t.topic || "—"}</td>
                      <td style={{ maxWidth: 260, whiteSpace: "pre-wrap" }}>{t.problem || "—"}</td>

                      <td>
                        <div className="d-flex flex-column gap-1 admin-row-actions">
                          {t.status !== "in_progress" && (
                            <Button
                              size="sm"
                              variant="warning"
                              onClick={() => updateStatus(t.ticketId, "in_progress")}
                            >
                              Mark In Progress
                            </Button>
                          )}

                          {t.status !== "closed" && (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => updateStatus(t.ticketId, "closed")}
                            >
                              Mark Closed
                            </Button>
                          )}

                          {t.status !== "open" && (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => updateStatus(t.ticketId, "open")}
                            >
                              Reopen
                            </Button>
                          )}

                          {t.smartTagId && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => openMessageModal(t)}
                            >
                              Text Owner
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>

        {/* SMS Compose Modal */}
        <Modal className="admin-modal" show={messageModal.show} onHide={closeMessageModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              Text Owner – SmartTag {messageModal.ticket?.smartTagId || ""}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
              This will send an SMS to the phone number registered for this SmartTag.
              Please avoid sharing sensitive information in messages.
            </p>

            <Form.Group controlId="adminSupportMessageText">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={messageModal.text}
                onChange={(e) =>
                  setMessageModal((prev) => ({
                    ...prev,
                    text: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeMessageModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={sendMessageToTag} disabled={sending}>
              {sending ? "Sending…" : "Send SMS"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
